<template>
  <div class="p-lg-4 reports-page bg-white table-shadow">
    <div class="mb-4 d-flex justify-content-between align-items-center">
      <div>
        <h1 class="mb-2">Reports</h1>
        <p class="font-weight-300 mb-0 text-muted">Automated scheduled insights.</p>
      </div>
      <button class="btn btn-green" @click="openAddModal">Add subscription</button>
    </div>

    <div v-if="isLoading" class="text-center py-5">Loading...</div>

    <div v-else class="d-flex flex-wrap gap-3 reports-list">
      <div
        v-for="sub in subscriptions"
        :key="sub.id"
        class="report-card border rounded p-3 mb-3 me-3 flex-grow-1 d-flex flex-column"
        :data-subscription-id="sub.id"
      >
        <div class="flex-grow-1">
          <h5 class="mb-1" v-text="sub.reportName || 'Unknown report'" />
          <p v-if="sub.recipient" class="small text-muted mb-1">
            <strong>Recipient:</strong> {{ sub.recipient }}
          </p>
        </div>
        <div class="mt-1 d-flex justify-content-between align-items-center action-row">
          <div>
            <button class="btn btn-green btn-sm me-2" :disabled="subsLoading" @click="editSubscription(sub)">
              Edit
            </button>
            <button class="btn btn-danger btn-sm" :disabled="subsLoading" @click="deleteSubscription(sub)">
              Delete
            </button>
          </div>
          <div class="small text-secondary text-end schedule-channel">
            <div><strong>Interval:</strong> <span v-text="sub.interval" /></div>
            <div><strong>Time:</strong> <span v-text="sub.time ? sub.time.slice(0, 5) : '—'" /></div>
          </div>
        </div>
      </div>
      <div v-if="subscriptions.length === 0" class="col-12 text-center py-4">No subscriptions yet</div>
    </div>
    <ReportSubscriptionActionModal />
  </div>
</template>

<script lang="ts">
import type { ReportSubscription } from '@/types/Models/ReportSubscription'
import { defineComponent, computed, onMounted, type ComputedRef } from 'vue'
import { REPORT_SUBSCRIPTION_ACTION } from '@/const/ModalTypes'
import { openModal } from '@/services/Modal'
import { useAccountStore } from '@/store/account'
import { useReportStore } from '@/store/report'
import { useReportSubscriptionStore } from '@/store/reportSubscription'
import ReportSubscriptionActionModal from '@/views/Modals/Report/ReportSubscriptionActionModal.vue'

export default defineComponent({
  name: 'ReportsIndex',
  components: { ReportSubscriptionActionModal },
  setup() {
    const reportStore = useReportStore()
    const accountStore = useAccountStore()
    const subscriptionStore = useReportSubscriptionStore()

    const subscriptions: ComputedRef<ReportSubscription[]> = computed(() => subscriptionStore.list)
    const isLoading: ComputedRef<boolean> = computed(() => reportStore.isLoading || subscriptionStore.isLoading)
    const subsLoading: ComputedRef<boolean> = computed(() => subscriptionStore.isLoading)

    const fetchAll = () => {
      reportStore.fetchAll()
      subscriptionStore.fetchAll()
    }

    onMounted(fetchAll)

    const openAddModal = () => {
      const email = accountStore.account.email || ''
      openModal(REPORT_SUBSCRIPTION_ACTION, { recipient: email })
    }

    const editSubscription = (sub: ReportSubscription) => {
      openModal(REPORT_SUBSCRIPTION_ACTION, { reportId: sub.reportId, subscription: sub })
    }

    const deleteSubscription = (sub: ReportSubscription) => {
      const reportName = sub.reportName || 'this report'
      openModal('confirmWrap', {
        title: 'Delete subscription',
        message: `Are you sure you want to delete the subscription for "${reportName}"?`,
        okButtonText: 'Delete',
        onConfirm: () => subscriptionStore.unsubscribe(sub.id),
      })
    }

    return {
      subscriptions,
      isLoading,
      subsLoading,
      openAddModal,
      editSubscription,
      deleteSubscription,
    }
  },
})
</script>

